import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MainLayout } from "@components/layout/MainLayout";
import { useAuthStore } from "@store/authStore";
import { usePermissions } from "@/utils/permissions";
import type { SectionName } from "@/types/user";

// ── Page imports (lazy-loaded for code splitting) ─────────────────
import { lazy, Suspense } from "react";

const CalendarPage    = lazy(() => import("@pages/calendar/CalendarPage"));
const MonthlyGridPage = lazy(() => import("@pages/monthly-grid/MonthlyGridPage"));
const TapeChartPage   = lazy(() => import("@pages/tape-chart/TapeChartPage"));
const DayDetailPage   = lazy(() => import("@pages/day-detail/DayDetailPage"));
const DashboardPage   = lazy(() => import("@pages/dashboard/DashboardPage"));
const CompetitorsPage = lazy(() => import("@pages/competitors/CompetitorsPage"));
const PricingPage     = lazy(() => import("@pages/pricing/PricingPage"));
const PmsPage         = lazy(() => import("@pages/pms/PmsPage"));
const UsersPage       = lazy(() => import("@pages/users/UsersPage"));
const AiPricingPage   = lazy(() => import("@pages/ai-pricing/AiPricingPage"));
const SetupPage       = lazy(() => import("@pages/setup/RoomRateSetupPage"));
const SmartLocksPage  = lazy(() => import("@pages/smart-locks/SmartLocksPage"));
const RespondPage     = lazy(() => import("@pages/respond/RespondPage"));
const MyHotelPage     = lazy(() => import("@pages/my-hotel/MyHotelPage"));
const DataHealthPage  = lazy(() => import("@pages/data-health/DataHealthPage"));
const QAHealthPage    = lazy(() => import("@pages/qa-health/QAHealthPage"));
const SyncLogPage     = lazy(() => import("@pages/sync-log/SyncLogPage"));
const LoginPage       = lazy(() => import("@pages/auth/LoginPage"));

// ── Loading fallback ──────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
    </div>
  );
}

// ── Auth guard ────────────────────────────────────────────────────
// _hasHydrated is set to true by the persist onRehydrateStorage callback
// once localStorage has been read.  Without this guard, the first render
// always sees isAuthenticated=false (the Zustand default) and redirects
// to /login before the persisted tokens are restored.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, _hasHydrated } = useAuthStore();

  if (!_hasHydrated) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// ── Permission guard ─────────────────────────────────────────────
function RequirePermission({ section, children }: { section: SectionName; children: React.ReactNode }) {
  const { canRead } = usePermissions();

  if (!canRead(section)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// ── Default redirect (to first accessible section) ────────────────
function DefaultRedirect() {
  const { canRead } = usePermissions();

  const sections: { section: SectionName; path: string }[] = [
    { section: "dashboard", path: "/dashboard" },
    { section: "monthly_grid", path: "/monthly-grid" },
    { section: "calendar", path: "/calendar" },
    { section: "competitors", path: "/competitors" },
    { section: "pricing", path: "/pricing" },
  ];

  for (const { section, path } of sections) {
    if (canRead(section)) return <Navigate to={path} replace />;
  }

  // Fallback to dashboard even without permission (will show access denied)
  return <Navigate to="/dashboard" replace />;
}

// ── App ───────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes — wrapped in MainLayout */}
          <Route
            element={
              <RequireAuth>
                <MainLayout />
              </RequireAuth>
            }
          >
            {/* Default redirect */}
            <Route index element={<DefaultRedirect />} />

            {/* Screen B — Monthly analytics grid (most-used screen) */}
            <Route path="monthly-grid" element={<RequirePermission section="monthly_grid"><MonthlyGridPage /></RequirePermission>} />

            {/* Tape Chart — Rates + Reservations grid */}
            <Route path="tape-chart" element={<RequirePermission section="monthly_grid"><TapeChartPage /></RequirePermission>} />

            {/* Screen A — Room-level operational calendar */}
            <Route path="calendar" element={<RequirePermission section="calendar"><CalendarPage /></RequirePermission>} />

            {/* Screen C — Day detail (opened from Screen B) */}
            <Route path="day/:date" element={<RequirePermission section="monthly_grid"><DayDetailPage /></RequirePermission>} />
            <Route path="day/:date/room-type/:roomTypeId" element={<RequirePermission section="monthly_grid"><DayDetailPage /></RequirePermission>} />

            {/* Screen D — Analytics dashboard */}
            <Route path="dashboard" element={<RequirePermission section="dashboard"><DashboardPage /></RequirePermission>} />

            {/* Screen E — Competitor config */}
            <Route path="competitors" element={<RequirePermission section="competitors"><CompetitorsPage /></RequirePermission>} />

            {/* Screen F — Base pricing */}
            <Route path="pricing" element={<RequirePermission section="pricing"><PricingPage /></RequirePermission>} />

            {/* AI Pricing Configuration */}
            <Route path="ai-pricing" element={<RequirePermission section="ai_pricing"><Navigate to="/ai-pricing/signals" replace /></RequirePermission>} />
            <Route path="ai-pricing/:tab" element={<RequirePermission section="ai_pricing"><AiPricingPage /></RequirePermission>} />

            {/* Screen G — PMS integration */}
            <Route path="pms" element={<RequirePermission section="pms"><PmsPage /></RequirePermission>} />

            {/* Screen H — User management */}
            <Route path="users" element={<UsersPage />} />

            {/* Room & Rate Setup */}
            <Route path="setup" element={<RequirePermission section="room_rates"><Navigate to="/setup/rooms" replace /></RequirePermission>} />
            <Route path="setup/:tab" element={<RequirePermission section="room_rates"><SetupPage /></RequirePermission>} />

            {/* My Hotel */}
            <Route path="my-hotel" element={<RequirePermission section="my_hotel"><Navigate to="/my-hotel/general" replace /></RequirePermission>} />
            <Route path="my-hotel/:tab" element={<RequirePermission section="my_hotel"><MyHotelPage /></RequirePermission>} />

            {/* Data Health */}
            <Route path="data-health" element={<RequirePermission section="data_health"><DataHealthPage /></RequirePermission>} />

            {/* Sync Log */}
            <Route path="sync-log" element={<RequirePermission section="data_health"><SyncLogPage /></RequirePermission>} />

            {/* Smart Locks */}
            <Route path="smart-locks" element={<RequirePermission section="smart_locks"><Navigate to="/smart-locks/mapping" replace /></RequirePermission>} />
            <Route path="smart-locks/:tab" element={<RequirePermission section="smart_locks"><SmartLocksPage /></RequirePermission>} />

            {/* ARIA Respond */}
            <Route path="respond" element={<RequirePermission section="respond"><Navigate to="/respond/inbox" replace /></RequirePermission>} />
            <Route path="respond/:tab" element={<RequirePermission section="respond"><RespondPage /></RequirePermission>} />

            {/* QA Health (superadmin only) */}
            <Route path="qa-health" element={<QAHealthPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
